import {
  CheckCircleOutlined,
  EmailOutlined,
  PhoneOutlined,
} from '@mui/icons-material';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  InputLabel,
  Link,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import React, { useEffect, useState } from 'react';

export type ContactIntent = 'reservation' | 'contact';

const API_BASE_URL = (
  process.env.REACT_APP_FEIZHOU_API_BASE_URL || 'https://fzapi.feizhou.chat'
).replace(/\/$/, '');

const subjects = [
  '产品演示',
  'Agent 办公咨询',
  '项目试点',
  '商务合作',
  '其他事项',
];

const contactTimes = [
  '工作日 9:00-12:00',
  '工作日 14:00-18:00',
  '电话沟通',
  '微信沟通',
  '时间不限',
];

interface AgentOfficeApplicationDialogProps {
  open: boolean;
  intent: ContactIntent;
  onClose: () => void;
}

interface FormState {
  name: string;
  phone: string;
  email: string;
  companyName: string;
  position: string;
  subject: string;
  message: string;
  preferredContactTime: string;
  privacyAccepted: boolean;
  website: string;
}

const initialForm: FormState = {
  name: '',
  phone: '',
  email: '',
  companyName: '',
  position: '',
  subject: '产品演示',
  message: '',
  preferredContactTime: '工作日 9:00-12:00',
  privacyAccepted: false,
  website: '',
};

export async function submitAgentOfficeApplication(
  form: FormState,
  intent: ContactIntent
) {
  const response = await fetch(
    `${API_BASE_URL}/api/v1/public/agent-office/applications`,
    {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        ...form,
        intent,
        pageUrl: window.location.href,
      }),
    }
  );
  const payload = await response.json().catch(() => null);
  if (!response.ok || !payload || payload.errCode !== 0) {
    throw new Error(payload?.errMsg || '提交失败，请稍后重试');
  }
  return payload.data;
}

const AgentOfficeApplicationDialog: React.FC<
  AgentOfficeApplicationDialogProps
> = ({ open, intent, onClose }) => {
  const [form, setForm] = useState<FormState>(initialForm);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [submitted, setSubmitted] = useState(false);

  useEffect(() => {
    if (open) {
      setForm(initialForm);
      setSubmitting(false);
      setError('');
      setSubmitted(false);
    }
  }, [open, intent]);

  const update = <K extends keyof FormState>(key: K, value: FormState[K]) => {
    setForm((current) => ({ ...current, [key]: value }));
  };

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!form.privacyAccepted) {
      setError('请阅读并同意隐私政策后再提交');
      return;
    }
    setSubmitting(true);
    setError('');
    try {
      await submitAgentOfficeApplication(form, intent);
      setSubmitted(true);
    } catch (submitError) {
      setError(
        submitError instanceof Error ? submitError.message : '提交失败，请稍后重试'
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={submitting ? undefined : onClose}
      fullWidth
      maxWidth="sm"
      aria-labelledby="agent-office-application-title"
    >
      {submitted ? (
        <Box sx={{ px: 3, py: 5, textAlign: 'center' }}>
          <CheckCircleOutlined
            sx={{ fontSize: 54, color: 'rgb(var(--brand-rgb))', mb: 2 }}
          />
          <Typography
            id="agent-office-application-title"
            component="h2"
            sx={{ fontSize: '1.45rem', fontWeight: 750, mb: 1 }}
          >
            申请已提交
          </Typography>
          <Typography sx={{ color: 'text.secondary', lineHeight: 1.8 }}>
            产品团队会通过您留下的联系方式与您沟通，请保持电话畅通。
          </Typography>
          <Button
            onClick={onClose}
            variant="contained"
            sx={{ mt: 3, borderRadius: '999px', px: 4 }}
          >
            完成
          </Button>
        </Box>
      ) : (
        <Box component="form" onSubmit={handleSubmit}>
          <DialogTitle id="agent-office-application-title">
            {intent === 'reservation' ? '预约产品交流' : '联系产品团队'}
          </DialogTitle>
          <DialogContent dividers>
            <Typography sx={{ color: 'text.secondary', mb: 2.5, lineHeight: 1.75 }}>
              留下基本信息和工作场景，我们会先了解需求，再安排产品演示或进一步沟通。
            </Typography>
            <Stack spacing={2}>
              <Box
                sx={{
                  display: 'grid',
                  gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' },
                  gap: 2,
                }}
              >
                <TextField
                  required
                  label="姓名"
                  value={form.name}
                  inputProps={{ maxLength: 64, autoComplete: 'name' }}
                  onChange={(event) => update('name', event.target.value)}
                />
                <TextField
                  required
                  label="手机号"
                  value={form.phone}
                  inputProps={{
                    maxLength: 32,
                    inputMode: 'tel',
                    autoComplete: 'tel',
                  }}
                  onChange={(event) => update('phone', event.target.value)}
                />
                <TextField
                  label="邮箱（选填）"
                  type="email"
                  value={form.email}
                  inputProps={{ maxLength: 128, autoComplete: 'email' }}
                  onChange={(event) => update('email', event.target.value)}
                />
                <TextField
                  required
                  label="公司名称"
                  value={form.companyName}
                  inputProps={{ maxLength: 128, autoComplete: 'organization' }}
                  onChange={(event) => update('companyName', event.target.value)}
                />
                <TextField
                  label="职位（选填）"
                  value={form.position}
                  inputProps={{ maxLength: 64 }}
                  onChange={(event) => update('position', event.target.value)}
                />
                <FormControl required>
                  <InputLabel id="application-subject-label">咨询事项</InputLabel>
                  <Select
                    labelId="application-subject-label"
                    label="咨询事项"
                    value={form.subject}
                    onChange={(event) => update('subject', event.target.value)}
                  >
                    {subjects.map((subject) => (
                      <MenuItem key={subject} value={subject}>
                        {subject}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Box>
              <TextField
                label="需求说明（选填）"
                value={form.message}
                multiline
                minRows={3}
                inputProps={{ maxLength: 1000 }}
                placeholder="例如：希望整理项目周报、会议结论或跟进待办"
                onChange={(event) => update('message', event.target.value)}
              />
              <FormControl>
                <InputLabel id="preferred-contact-time-label">
                  方便联系时间
                </InputLabel>
                <Select
                  labelId="preferred-contact-time-label"
                  label="方便联系时间"
                  value={form.preferredContactTime}
                  onChange={(event) =>
                    update('preferredContactTime', event.target.value)
                  }
                >
                  {contactTimes.map((time) => (
                    <MenuItem key={time} value={time}>
                      {time}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <Box
                aria-hidden="true"
                sx={{
                  position: 'absolute',
                  left: '-10000px',
                  width: 1,
                  height: 1,
                  overflow: 'hidden',
                }}
              >
                <TextField
                  label="网站"
                  tabIndex={-1}
                  autoComplete="off"
                  value={form.website}
                  onChange={(event) => update('website', event.target.value)}
                />
              </Box>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={form.privacyAccepted}
                    onChange={(event) =>
                      update('privacyAccepted', event.target.checked)
                    }
                  />
                }
                label={
                  <Typography component="span" sx={{ fontSize: '0.86rem' }}>
                    我已阅读并同意
                    <Link
                      href="/legal/privacy.html"
                      target="_blank"
                      rel="noopener noreferrer"
                      underline="hover"
                      sx={{ mx: 0.4 }}
                    >
                      隐私政策
                    </Link>
                    ，同意产品团队联系我。
                  </Typography>
                }
              />
              {error && <Alert severity="error">{error}</Alert>}
              <Stack
                direction={{ xs: 'column', sm: 'row' }}
                spacing={1.5}
                sx={{ color: 'text.secondary', fontSize: '0.78rem' }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.7 }}>
                  <PhoneOutlined fontSize="small" />
                  0755-23606709
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.7 }}>
                  <EmailOutlined fontSize="small" />
                  sales@qdtg.com
                </Box>
              </Stack>
            </Stack>
          </DialogContent>
          <DialogActions sx={{ px: 3, py: 2 }}>
            <Button onClick={onClose} disabled={submitting}>
              取消
            </Button>
            <Button
              type="submit"
              variant="contained"
              disabled={submitting}
              startIcon={
                submitting ? <CircularProgress size={16} color="inherit" /> : null
              }
              sx={{ borderRadius: '999px', px: 3 }}
            >
              {submitting ? '提交中' : '提交申请'}
            </Button>
          </DialogActions>
        </Box>
      )}
    </Dialog>
  );
};

export default AgentOfficeApplicationDialog;
